import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import UndoRounded from '@mui/icons-material/UndoRounded';
import { Box, Chip, Link, Skeleton, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router';
import { api, unwrapJson, type Schemas } from '../../../api/client';
import { businessDate } from '../../../lib/format';
import { useShop } from '../../../shop/ShopProvider';
import type { SubRow } from '../components/ReportTable';
import { linkTo } from '../components/useEntityLink';
import { M } from './common';

type Documents = Pick<Schemas['SalesByCustomerDocuments'], 'totals'> & {
  data: (
    | Schemas['SalesByCustomerDocuments']['data'][number]
    | Schemas['PurchasesBySupplierDocuments']['data'][number]
  )[];
};
type Kind = Documents['data'][number]['kind'];

const KINDS: Record<Kind, { label: string; entity: string; note: boolean }> = {
  invoice: { label: 'Invoice', entity: 'sales_invoice', note: false },
  credit_note: { label: 'Credit note', entity: 'credit_note', note: true },
  bill: { label: 'Bill', entity: 'supplier_invoice', note: false },
  debit_note: { label: 'Debit note', entity: 'debit_note', note: true },
  receipt: { label: 'Goods receipt', entity: 'goods_receipt', note: false },
  purchase_return: { label: 'Return', entity: 'purchase_return', note: true },
};

/**
 * The documents behind one row of Sales by customer or Purchases by supplier, as sub-rows under it:
 * each invoice (bill, receipt) and each credit note (debit note, return, subtracted), adding up to
 * the row's net, tax and total.
 */
export function PartyDocumentRows({
  side,
  contactId,
  range,
  source = 'bills',
  subRow,
}: {
  side: 'sales' | 'purchases';
  contactId: string;
  range: { from?: string; to?: string };
  source?: 'bills' | 'receipts';
  subRow: SubRow;
}) {
  const { format } = useShop();
  const q = useQuery({
    queryKey: ['reports', 'party-documents', side, contactId, range, source],
    queryFn: (): Promise<Documents> =>
      side === 'sales'
        ? unwrapJson(
            api.GET('/reports/sales/by-customer/documents', {
              params: { query: { contactId, ...range } },
            }),
          )
        : unwrapJson(
            api.GET('/reports/purchases/by-supplier/documents', {
              params: { query: { contactId, source, ...range } },
            }),
          ),
  });

  if (q.isLoading)
    return [0, 1].map((i) =>
      subRow(`loading-${String(i)}`, {
        name: <Skeleton width={180} />,
        net: <Skeleton width={70} sx={{ ml: 'auto' }} />,
        tax: <Skeleton width={50} sx={{ ml: 'auto' }} />,
        total: <Skeleton width={80} sx={{ ml: 'auto' }} />,
      }),
    );
  if (q.error || !q.data)
    return subRow('error', {
      name: (
        <Typography variant="body2" color="error">
          Could not load the documents.{' '}
          <Link component="button" onClick={() => void q.refetch()}>
            Try again
          </Link>
        </Typography>
      ),
    });

  return q.data.data.map((d) => {
    const kind = KINDS[d.kind];
    const to = linkTo(kind.entity, d.id);
    const Icon = kind.note ? UndoRounded : DescriptionOutlined;
    return subRow(d.id, {
      name: (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
          <Icon sx={{ fontSize: 18, color: kind.note ? 'warning.main' : 'text.secondary' }} />
          <Box sx={{ minWidth: 0 }}>
            {to ? (
              <Link component={RouterLink} to={to} underline="hover" sx={{ fontWeight: 600 }}>
                {d.number}
              </Link>
            ) : (
              <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                {d.number}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" component="div">
              {businessDate(d.docDate, format)}
            </Typography>
          </Box>
        </Box>
      ),
      count: (
        <Chip
          size="small"
          label={kind.label}
          color={kind.note ? 'warning' : 'default'}
          variant="outlined"
          sx={{ height: 22, fontSize: 11.5, fontWeight: 600 }}
        />
      ),
      net: <M v={d.net} />,
      tax: <M v={d.tax} />,
      total: <M v={d.total} />,
    });
  });
}
